import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import billingService from '../../services/billingService';
import { sendReportEmail } from '../../utils/reportEmailSend';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDateOnlyDash, formatDateOnlyUs, normalizeDateOnly } from '../../utils/dateOnlyUtils';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';
import { getEmailValidationError, sanitizeEmailInput } from '../../utils/emailValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import '../../components/Common/Table/Table.css';
import '../../components/EmailModal/EmailModal.css';
import './GenerateBillingModal.css';

const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];
const BANNER_BLUE = [3, 4, 94];

const RPM_HEADERS = [
  'Status', 'Name', 'Provider', 'Caregiver', 'Serial #', 'Vitals',
  'RPM Start Date', '# Of Readings', 'Total Service Time', 'Eligible CPT codes'
];

const MANDATORY_EMAIL_COLUMNS = ['name', 'patient name'];

const isMandatoryEmailColumn = (column) => (
  MANDATORY_EMAIL_COLUMNS.includes(String(column || '').toLowerCase().trim())
);

const DEFAULT_FILTERS = {
  status: 'Active',
  name: '',
  readingsMin: '',
  readingsMax: '',
  serviceMin: '',
  serviceMax: '',
  clinicalMin: '',
  clinicalMax: '',
  cptCodes: [],
  includeNonCompliant: true
};

const filtersAreDefault = (filters) => (
  filters.status === DEFAULT_FILTERS.status
  && filters.name === DEFAULT_FILTERS.name
  && filters.readingsMin === DEFAULT_FILTERS.readingsMin
  && filters.readingsMax === DEFAULT_FILTERS.readingsMax
  && filters.serviceMin === DEFAULT_FILTERS.serviceMin
  && filters.serviceMax === DEFAULT_FILTERS.serviceMax
  && filters.clinicalMin === DEFAULT_FILTERS.clinicalMin
  && filters.clinicalMax === DEFAULT_FILTERS.clinicalMax
  && filters.cptCodes.length === 0
  && filters.includeNonCompliant === DEFAULT_FILTERS.includeNonCompliant
);

const readingCount = (patient) => Number(patient?.measurementDays ?? patient?.measurement_days ?? 0) || 0;

const numberOrNull = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const inRange = (value, min, max) => {
  const amount = numberOrNull(value);
  const current = amount == null ? 0 : amount;
  const minValue = numberOrNull(min);
  const maxValue = numberOrNull(max);
  if (minValue != null && current < minValue) return false;
  if (maxValue != null && current > maxValue) return false;
  return true;
};

const rpmCptCodesFromMeasures = (readings, serviceTime, enrolledInPeriod) => {
  const days = Number.parseInt(readings, 10);
  const minutes = Number.parseInt(serviceTime, 10);
  const dayCount = Number.isFinite(days) && days > 0 ? days : 0;
  const minuteCount = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  const codes = [];
  if (enrolledInPeriod) codes.push('99453');
  if (dayCount >= 16) codes.push('99454');
  else if (dayCount >= 2) codes.push('99445');
  if (minuteCount >= 20) codes.push('99457');
  else if (minuteCount >= 10) codes.push('99470');
  if (minuteCount >= 40) codes.push('99458');
  if (minuteCount >= 60) codes.push('99458');
  return codes;
};

const matchesFilters = (row, filters, isRpm) => {
  if (filters.status && String(row.status || '').toLowerCase() !== filters.status.toLowerCase()) return false;
  const name = filters.name.trim().toLowerCase();
  if (name && !String(row.patientName || '').toLowerCase().includes(name)) return false;
  if (isRpm && !inRange(row.readings, filters.readingsMin, filters.readingsMax)) return false;
  if (!inRange(row.serviceTime, filters.serviceMin, filters.serviceMax)) return false;
  if (!isRpm && !inRange(row.clinicalMinutes, filters.clinicalMin, filters.clinicalMax)) return false;
  const codes = row.cptCodes || [];
  if (filters.cptCodes.length && !codes.some((code) => filters.cptCodes.includes(code))) return false;
  return true;
};

const rangeError = (filters, isRpm) => {
  if (isRpm && numberOrNull(filters.readingsMin) != null && numberOrNull(filters.readingsMax) != null
    && Number(filters.readingsMin) > Number(filters.readingsMax)) {
    return 'Readings minimum cannot be higher than the maximum';
  }
  if (numberOrNull(filters.serviceMin) != null && numberOrNull(filters.serviceMax) != null
    && Number(filters.serviceMin) > Number(filters.serviceMax)) {
    return 'Service time minimum cannot be higher than the maximum';
  }
  return '';
};

const formatShortUs = (value) => {
  const us = formatDateOnlyUs(value);
  if (!us) return value ? String(value) : '';
  const [month, day, year] = us.split('/');
  return `${Number(month)}/${Number(day)}/${year}`;
};

const sheetHeading = (practiceName, programLabel, startYmd) => {
  const name = practiceName || 'Practice';
  const program = String(programLabel || 'RPM').toUpperCase();
  const ymd = normalizeDateOnly(startYmd);
  if (!ymd) return `${name} ${program}`;
  const [year, month] = ymd.split('-');
  const monthName = MONTHS[Number(month) - 1] || '';
  return `${name} ${program} ${monthName} ${year}`.replace(/\s+/g, ' ').trim();
};

const dosLabel = (endYmd) => {
  const dash = formatDateOnlyDash(endYmd);
  return dash ? `DOS: ${dash}` : 'DOS';
};

const rangeLabel = (startYmd, endYmd) => {
  const start = formatDateOnlyDash(startYmd);
  const end = formatDateOnlyDash(endYmd);
  if (start && end) return `Billing period: ${start} – ${end}`;
  return '';
};

const parseInitialCptCodes = (patient) => {
  if (Array.isArray(patient?.cptCodes) && patient.cptCodes.length) {
    return patient.cptCodes.map((code) => String(code).trim()).filter(Boolean);
  }
  const raw = patient?.eligibleCptCodes || patient?.eligible_cpt_codes || '';
  if (!raw || raw === 'N/A') return [];
  return String(raw).split(/[,\s]+/).map((code) => code.trim()).filter(Boolean);
};

const countCodes = (codes) => {
  const counts = {};
  (codes || []).forEach((code) => {
    const key = String(code || '').trim();
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

const orderedCodes = (codes, catalog) => {
  const counts = countCodes(codes);
  const known = (catalog || []).filter((code) => counts[code]);
  const extras = Object.keys(counts)
    .filter((code) => !(catalog || []).includes(code))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return [...known, ...extras];
};

const formatCptList = (codes, catalog) => {
  const counts = countCodes(codes);
  const parts = [];
  orderedCodes(codes, catalog).forEach((code) => {
    const count = counts[code] || 0;
    for (let i = 0; i < count; i += 1) parts.push(code);
  });
  return parts.join(', ');
};

const cptPillEntries = (codes, catalog) => {
  const counts = countCodes(codes);
  const entries = [];
  orderedCodes(codes, catalog).forEach((code) => {
    const count = counts[code] || 0;
    for (let occurrence = 0; occurrence < count; occurrence += 1) {
      entries.push({ code, occurrence });
    }
  });
  return entries;
};

const sortByName = (a, b) => String(a.patientName || '').localeCompare(String(b.patientName || ''), undefined, {
  sensitivity: 'base'
});

const sortByReadings = (a, b) => {
  const diff = (Number(a.readings) || 0) - (Number(b.readings) || 0);
  if (diff !== 0) return diff;
  return sortByName(a, b);
};

const sortByOrder = (a, b, isRpm) => {
  const diff = (Number(a.order) || 0) - (Number(b.order) || 0);
  if (a.order != null && b.order != null && diff !== 0) return diff;
  return isRpm ? sortByReadings(a, b) : sortByName(a, b);
};

const stampSheetOrder = (list, isRpm) => {
  const ordered = [...list].sort((a, b) => {
    const sectionDiff = (a.section === 'noncompliant' ? 1 : 0) - (b.section === 'noncompliant' ? 1 : 0);
    if (sectionDiff !== 0) return sectionDiff;
    return isRpm && a.section !== 'noncompliant' ? sortByReadings(a, b) : sortByName(a, b);
  });
  return ordered.map((row, index) => ({ ...row, order: index }));
};

const arrangeRows = (list, filters, isRpm) => {
  const matched = list.filter((row) => matchesFilters(row, filters, isRpm));
  const standard = matched
    .filter((row) => row.section !== 'noncompliant')
    .sort((a, b) => sortByOrder(a, b, isRpm));
  const noncompliant = filters.includeNonCompliant
    ? matched.filter((row) => row.section === 'noncompliant').sort((a, b) => sortByOrder(a, b, false))
    : [];
  return [...standard, ...noncompliant];
};

const toSheetRow = (patient) => {
  const readings = readingCount(patient);
  const serviceMinutes = Number(patient.serviceTime ?? patient.service_time ?? 0) || 0;
  const savedCptCodes = parseInitialCptCodes(patient);
  const readingsBlank = readings <= 0;
  const serviceBlank = serviceMinutes <= 0;
  const codesBlank = savedCptCodes.length === 0;
  const section = readingsBlank && serviceBlank && codesBlank ? 'noncompliant' : 'standard';
  return {
    key: `patient-${patient.id}`,
    patientName: patient.patientName || '',
    status: patient.status || '',
    rpmStartDate: formatShortUs(patient.rpmStartDate || patient.rpm_start_date || ''),
    readings: String(readings),
    serviceTime: String(serviceMinutes),
    clinicalMinutes: String(patient.clinicalMinutes ?? 0),
    providerMinutes: String(patient.providerMinutes ?? 0),
    provider: patient.providerDisplay || patient.provider_name || '—',
    caregiver: patient.caregiverDisplay || patient.caregiver_name || '—',
    serial: patient.serial || '—',
    vitals: patient.vitals || '—',
    savedCptCodes,
    enrolledInPeriod: savedCptCodes.includes('99453'),
    cptCodes: savedCptCodes,
    sectionManual: false,
    section
  };
};

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const fileStamp = (practiceName, program) => {
  const practice = String(practiceName || 'practice').replace(/[^\w-]+/g, '_').replace(/_+/g, '_').slice(0, 40);
  const day = new Date().toISOString().slice(0, 10);
  return `${practice}_${String(program || 'rpm').toUpperCase()}_billing_${day}`;
};

const isEmail = (value) => !getEmailValidationError(value, { required: true });

const splitEmailDraft = (value) => String(value || '').split(',').map((email) => email.trim()).filter(Boolean);

const bannerCell = (text, colSpan) => ([{
  content: text,
  colSpan,
  styles: {
    fillColor: BANNER_BLUE,
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    halign: 'center',
    fontSize: 12,
    cellPadding: { top: 7, right: 4, bottom: 7, left: 4 }
  }
}]);

const GenerateBillingModal = ({
  isOpen,
  onClose,
  practiceId,
  practiceName,
  program,
  periodLabel,
  periodStart,
  periodEnd,
  cptCatalog,
  loadPatients
}) => {
  const isRpm = program === 'rpm';
  const programLabel = String(program || 'rpm').toUpperCase();
  const catalog = Array.isArray(cptCatalog) && cptCatalog.length ? cptCatalog : [];
  const baseHeaders = isRpm
    ? RPM_HEADERS
    : program === 'pcm'
      ? ['Status', 'Name', 'Provider', 'Caregiver', 'Clinical Min', 'Provider Min', 'Service Time', 'Eligible CPT codes']
      : ['Status', 'Name', 'Provider', 'Caregiver', 'Clinical Min', 'Provider Min', 'Eligible CPT codes'];
  const defaultSelectedHeaders = isRpm
    ? ['Status', 'Name', 'RPM Start Date', '# Of Readings', 'Total Service Time', 'Eligible CPT codes']
    : baseHeaders;
  const heading = sheetHeading(practiceName, programLabel, periodStart);
  const dos = dosLabel(periodEnd);
  const periodRange = rangeLabel(periodStart, periodEnd) || periodLabel || '';

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [cptMenuOpen, setCptMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [rows, setRows] = useState([]);
  const [cptPricingEnabled, setCptPricingEnabled] = useState(false);
  const [cptPrices, setCptPrices] = useState({});
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [receiptDraft, setReceiptDraft] = useState([]);
  const [showAdminPicker, setShowAdminPicker] = useState(false);
  const [adminPickerTab, setAdminPickerTab] = useState('recipients');
  const [pdfPreviewBlobUrl, setPdfPreviewBlobUrl] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);
  const [customRecipients, setCustomRecipients] = useState([]);
  const [customEmailDraft, setCustomEmailDraft] = useState('');
  const [customEmailError, setCustomEmailError] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const headers = cptPricingEnabled ? [...baseHeaders, 'Price'] : baseHeaders;
  const [selectedEmailColumns, setSelectedEmailColumns] = useState(defaultSelectedHeaders);
  const [emailColOpen, setEmailColOpen] = useState(false);
  const cptMenuRef = useRef(null);
  const exportMenuRef = useRef(null);
  const emailColRef = useRef(null);
  const loadPatientsRef = useRef(loadPatients);
  loadPatientsRef.current = loadPatients;

  useEffect(() => {
    if (!isOpen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const scrollY = window.scrollY;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    const canScrollInsideDialog = (target, deltaY) => {
      const dialog = target?.closest?.('.gbm-dialog');
      if (!dialog) return false;
      let node = target;
      while (node && node !== dialog.parentElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const scrollable = (overflowY === 'auto' || overflowY === 'scroll')
          && node.scrollHeight > node.clientHeight + 1;
        if (scrollable) {
          const atTop = node.scrollTop <= 0;
          const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          if (deltaY < 0 && !atTop) return true;
          if (deltaY > 0 && !atBottom) return true;
        }
        if (node === dialog) break;
        node = node.parentElement;
      }
      return false;
    };

    const stopBackgroundScroll = (event) => {
      if (event.type !== 'wheel') {
        if (event.target?.closest?.('.gbm-dialog')) return;
        event.preventDefault();
        return;
      }
      if (canScrollInsideDialog(event.target, event.deltaY || 0)) return;
      event.preventDefault();
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('wheel', stopBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', stopBackgroundScroll, { passive: false, capture: true });
    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, scrollY);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('wheel', stopBackgroundScroll, { capture: true });
      document.removeEventListener('touchmove', stopBackgroundScroll, { capture: true });
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setFilters(DEFAULT_FILTERS);
    setFiltersExpanded(false);
    setCptMenuOpen(false);
    setExportMenuOpen(false);
    setHasGenerated(false);
    setRows([]);
    setCptPricingEnabled(false);
    setCptPrices({});
    setShowReceiptPreview(false);
    setReceiptDraft([]);
    setShowAdminPicker(false);
    setAdmins([]);
    setSelectedAdminIds([]);
    setCustomRecipients([]);
    setCustomEmailDraft('');
    setSendingEmail(false);
    setSelectedEmailColumns(defaultSelectedHeaders);
    setEmailColOpen(false);
    setLoading(true);

    (async () => {
      try {
        const patients = await loadPatientsRef.current();
        if (cancelled) return;
        const nextRows = stampSheetOrder(
          (Array.isArray(patients) ? patients : []).map((patient) => toSheetRow(patient)),
          isRpm
        );
        setRows(nextRows);
        setHasGenerated(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Generate billing failed:', error);
        toast.error('Failed to generate billing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isRpm, practiceId, program, periodStart, periodEnd]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedEmailColumns((current) => {
      const withoutPrice = current.filter((column) => column !== 'Price');
      return cptPricingEnabled ? [...withoutPrice, 'Price'] : withoutPrice;
    });
  }, [cptPricingEnabled, isOpen]);

  useEffect(() => {
    if (!cptMenuOpen && !exportMenuOpen && !emailColOpen) return undefined;
    const onPointerDown = (event) => {
      if (cptMenuRef.current && !cptMenuRef.current.contains(event.target)) {
        setCptMenuOpen(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
      if (emailColRef.current && !emailColRef.current.contains(event.target)) {
        setEmailColOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [cptMenuOpen, exportMenuOpen, emailColOpen]);

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const removeCptOccurrence = (row, code, occurrence) => {
    let seen = 0;
    const next = (row.cptCodes || []).filter((item) => {
      if (item !== code) return true;
      const keep = seen !== occurrence;
      seen += 1;
      return keep;
    });
    updateRow(row.key, { cptCodes: next });
  };

  const updateRpmMeasures = (row, patch) => {
    const readings = Object.prototype.hasOwnProperty.call(patch, 'readings') ? patch.readings : row.readings;
    const serviceTime = Object.prototype.hasOwnProperty.call(patch, 'serviceTime') ? patch.serviceTime : row.serviceTime;
    updateRow(row.key, {
      ...patch,
      cptCodes: rpmCptCodesFromMeasures(readings, serviceTime, row.enrolledInPeriod)
    });
  };

  const displayRows = useMemo(
    () => arrangeRows(rows, filters, isRpm),
    [rows, filters, isRpm]
  );

  const cptCodePatientCounts = useMemo(() => {
    const counts = {};
    rows.forEach((row) => {
      if (row.section === 'noncompliant') return;
      [...new Set((row.cptCodes || []).map((code) => String(code || '').trim()).filter(Boolean))]
        .forEach((code) => {
          counts[code] = (counts[code] || 0) + 1;
        });
    });
    return counts;
  }, [rows]);

  const priceForRow = (row) => {
    if (row.section === 'noncompliant') return null;
    const codes = (row.cptCodes || []).map((code) => String(code || '').trim()).filter(Boolean);
    if (codes.length === 0) return null;
    let total = 0;
    for (const code of codes) {
      const price = numberOrNull(cptPrices[code]);
      if (price == null) return null;
      total += price;
    }
    return total;
  };

  const cptCodesForDisplay = (row) => (
    row.section === 'noncompliant' ? [] : (row.cptCodes || [])
  );

  const handleGenerateReceipt = () => {
    if (!hasGenerated || loading) {
      toast.info('Generate the billing sheet before creating a receipt');
      return;
    }

    const quantities = {};
    displayRows
      .filter((row) => row.section !== 'noncompliant')
      .forEach((row) => {
        cptCodesForDisplay(row).forEach((rawCode) => {
          const code = String(rawCode || '').trim();
          if (code) quantities[code] = (quantities[code] || 0) + 1;
        });
      });

    const codes = Object.keys(quantities).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    if (!codes.length) {
      toast.info('There are no compliant CPT services to include in a receipt');
      return;
    }

    const missingPrice = codes.find((code) => numberOrNull(cptPrices[code]) == null);
    if (missingPrice) {
      toast.warning(`Enter a price for CPT ${missingPrice} before generating the receipt`);
      return;
    }

    const receiptLines = codes.map((code, index) => {
      const quantity = quantities[code];
      const rate = numberOrNull(cptPrices[code]);
      return {
        id: `cpt-${code}-${index}`,
        serviceDate: normalizeDateOnly(periodEnd) || String(periodEnd || ''),
        code,
        product: `CPT ${code}`,
        description: `${programLabel} eligible patient service`,
        quantity,
        rate: String(rate)
      };
    });

    setReceiptDraft(receiptLines);
    setShowReceiptPreview(true);
  };

  const updateReceiptLine = (lineId, field, value) => {
    setReceiptDraft((current) => current.map((line) => (
      line.id === lineId ? { ...line, [field]: value } : line
    )));
  };

  const addReceiptLine = () => {
    setReceiptDraft((current) => ([
      ...current,
      {
        id: `custom-${Date.now()}-${current.length}`,
        serviceDate: normalizeDateOnly(periodEnd) || String(periodEnd || ''),
        code: '',
        product: '',
        description: '',
        quantity: '1',
        rate: ''
      }
    ]));
  };

  const removeReceiptLine = (lineId) => {
    setReceiptDraft((current) => current.filter((line) => line.id !== lineId));
  };

  const receiptLineAmount = (line) => (
    (numberOrNull(line.quantity) || 0) * (numberOrNull(line.rate) || 0)
  );

  const buildReceiptPdf = (receiptLines) => {
    const total = receiptLines.reduce((sum, line) => sum + receiptLineAmount(line), 0);
    const receipt = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pageWidth = receipt.internal.pageSize.getWidth();

    receipt.setFont('helvetica', 'bold');
    receipt.setFontSize(19);
    receipt.text('Billing Receipt', pageWidth / 2, 38, { align: 'center' });
    receipt.setFont('helvetica', 'normal');
    receipt.setFontSize(10);
    receipt.text(`Practice: ${practiceName || 'Practice'}`, 38, 59);
    receipt.text(`Program: ${programLabel}`, 38, 75);
    receipt.text(`Service date: ${formatDateOnlyUs(periodEnd) || periodEnd || '—'}`, pageWidth - 38, 59, { align: 'right' });
    receipt.text(`Billing period: ${periodRange || '—'}`, pageWidth - 38, 75, { align: 'right' });

    autoTable(receipt, {
      startY: 90,
      head: [['#', 'Service Date', 'Product/service', 'Description', 'Qty', 'Rate', 'Amount']],
      body: receiptLines.map((line, index) => ([
        String(index + 1),
        formatDateOnlyUs(line.serviceDate) || line.serviceDate || '—',
        line.product || '',
        line.description || '',
        String(numberOrNull(line.quantity) || 0),
        `$${(numberOrNull(line.rate) || 0).toFixed(2)}`,
        `$${receiptLineAmount(line).toFixed(2)}`
      ])),
      foot: [['', '', '', '', '', 'Total', `$${total.toFixed(2)}`]],
      theme: 'grid',
      headStyles: { fillColor: [0, 115, 175], textColor: [255, 255, 255], fontStyle: 'bold' },
      footStyles: { fillColor: [245, 248, 251], textColor: [15, 23, 42], fontStyle: 'bold' },
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 7, lineColor: [210, 218, 228] },
      columnStyles: {
        0: { cellWidth: 30, halign: 'center' },
        1: { cellWidth: 78 },
        2: { cellWidth: 100 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 42, halign: 'right' },
        5: { cellWidth: 58, halign: 'right' },
        6: { cellWidth: 66, halign: 'right' }
      },
      margin: { left: 38, right: 38 }
    });
    return receipt;
  };

  const handleSaveReceipt = () => {
    if (receiptDraft.length === 0) {
      toast.warning('Add at least one service line before saving the receipt');
      return;
    }
    const invalidLine = receiptDraft.find((line) => (
      !String(line.serviceDate || '').trim()
      || !String(line.product || '').trim()
      || !String(line.description || '').trim()
      || numberOrNull(line.quantity) == null
      || Number(line.quantity) <= 0
      || numberOrNull(line.rate) == null
      || Number(line.rate) < 0
    ));
    if (invalidLine) {
      toast.warning('Complete each line with a service date, product, description, valid quantity, and rate');
      return;
    }
    buildReceiptPdf(receiptDraft).save(`${fileStamp(practiceName, `${program}_receipt`)}.pdf`);
    setShowReceiptPreview(false);
    toast.success('Receipt PDF saved');
  };

  const validateCptPricing = () => {
    if (!cptPricingEnabled) return true;
    const invalidCodePrice = Object.entries(cptPrices).find(([, value]) => (
      String(value).trim() !== '' && (numberOrNull(value) == null || Number(value) < 0)
    ));
    if (invalidCodePrice) {
      toast.error('CPT code prices must be valid non-negative amounts');
      return false;
    }
    return true;
  };

  const setFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const allCptSelected = catalog.length > 0 && catalog.every((code) => filters.cptCodes.includes(code));

  const toggleFilterCpt = (code) => {
    setFilters((prev) => {
      const current = prev.cptCodes;
      const next = current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code];
      return { ...prev, cptCodes: next };
    });
  };

  const toggleAllFilterCpt = () => {
    setFilters((prev) => {
      const allSelected = catalog.length > 0 && catalog.every((code) => prev.cptCodes.includes(code));
      return { ...prev, cptCodes: allSelected ? [] : [...catalog] };
    });
  };

  const handleGenerate = async () => {
    const error = rangeError(filters, isRpm);
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      const patients = await loadPatients();
      const nextRows = stampSheetOrder(
        (Array.isArray(patients) ? patients : []).map((patient) => toSheetRow(patient)),
        isRpm
      );
      setRows(nextRows);
      setHasGenerated(true);
      if (!arrangeRows(nextRows, filters, isRpm).length) {
        toast.info('No patients matched these filters');
      }
    } catch (error) {
      console.error('Generate billing failed:', error);
      toast.error('Failed to generate billing');
    } finally {
      setLoading(false);
    }
  };

  const nonCompliantCount = displayRows.filter((row) => row.section === 'noncompliant').length;

  const rowValues = (row) => {
    const cpt = formatCptList(cptCodesForDisplay(row), catalog);
    const values = isRpm
      ? [row.status || '', row.patientName || '', row.provider || '', row.caregiver || '',
        row.serial || '', row.vitals || '', row.rpmStartDate || '',
        row.readings === '' ? '' : String(row.readings),
        row.serviceTime === '' ? '' : String(row.serviceTime), cpt]
      : program === 'pcm'
        ? [row.status || '', row.patientName || '', row.provider || '', row.caregiver || '',
          row.clinicalMinutes || '0', row.providerMinutes || '0', row.serviceTime || '0', cpt]
        : [row.status || '', row.patientName || '', row.provider || '', row.caregiver || '',
          row.clinicalMinutes || '0', row.providerMinutes || '0', cpt];
    if (cptPricingEnabled) {
      const price = priceForRow(row);
      values.push(price == null ? '—' : price.toFixed(2));
    }
    return values;
  };

  const chosenHeaders = (selectedHeaders) => {
    const wanted = new Set(
      Array.isArray(selectedHeaders) && selectedHeaders.length ? selectedHeaders : headers
    );
    headers.forEach((header) => {
      if (isMandatoryEmailColumn(header)) wanted.add(header);
    });
    return headers.filter((header) => wanted.has(header));
  };

  const buildSheetCsv = (selectedHeaders) => {
    const sheetHeaders = chosenHeaders(selectedHeaders);
    const indexes = sheetHeaders.map((header) => headers.indexOf(header));
    const lines = [
      [heading],
      [dos],
      [periodRange],
      sheetHeaders
    ];
    let openedNonCompliant = false;
    displayRows.forEach((row) => {
      if (row.section === 'noncompliant' && !openedNonCompliant) {
        openedNonCompliant = true;
        lines.push(['Noncompliant Patients']);
      }
      const values = rowValues(row);
      lines.push(indexes.map((index) => values[index]));
    });
    return lines.map((line) => line.map(csvCell).join(',')).join('\r\n');
  };

  const handleExportCsv = async () => {
    if (!displayRows.length) {
      toast.info('Generate the sheet before exporting');
      return;
    }
    if (!validateCptPricing()) return;
    const csv = buildSheetCsv(selectedEmailColumns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileStamp(practiceName, program)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const buildSheetPdf = (selectedHeaders) => {
    const sheetHeaders = chosenHeaders(selectedHeaders);
    const indexes = sheetHeaders.map((header) => headers.indexOf(header));
    const colSpan = sheetHeaders.length;
    const body = [
      bannerCell(heading, colSpan),
      bannerCell(dos, colSpan),
      [{
        content: periodRange,
        colSpan,
        styles: {
          halign: 'center',
          fontStyle: 'bold',
          fillColor: [255, 255, 255],
          fontSize: 10
        }
      }],
      sheetHeaders.map((header) => ({
        content: header,
        styles: {
          fontStyle: 'bold',
          halign: 'center',
          fillColor: [255, 255, 255],
          fontSize: 9
        }
      }))
    ];

    let openedNonCompliant = false;
    displayRows.forEach((row) => {
      if (row.section === 'noncompliant' && !openedNonCompliant) {
        openedNonCompliant = true;
        body.push(bannerCell('Noncompliant Patients', colSpan));
      }
      const values = rowValues(row);
      body.push(indexes.map((index) => values[index]));
    });

    const columnStyles = {};
    const nameIndex = sheetHeaders.findIndex((header) => isMandatoryEmailColumn(header));
    const cptIndex = sheetHeaders.findIndex((header) => header === 'Eligible CPT codes');
    const priceIndex = sheetHeaders.findIndex((header) => header === 'Price');
    if (nameIndex >= 0) columnStyles[nameIndex] = { halign: 'left' };
    if (cptIndex >= 0) columnStyles[cptIndex] = { halign: 'right' };
    if (priceIndex >= 0) columnStyles[priceIndex] = { halign: 'right' };

    const doc = new jsPDF({
      orientation: sheetHeaders.length > 7 ? 'landscape' : 'portrait',
      unit: 'pt',
      format: 'letter'
    });
    autoTable(doc, {
      startY: 28,
      body,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        halign: 'center',
        valign: 'middle',
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
        overflow: 'linebreak'
      },
      columnStyles,
      margin: { left: 28, right: 28, top: 28, bottom: 28 },
      rowPageBreak: 'avoid'
    });
    return doc;
  };

  const handleExportPdf = async () => {
    if (!displayRows.length) {
      toast.info('Generate the sheet before exporting');
      return;
    }
    if (!validateCptPricing()) return;
    buildSheetPdf(selectedEmailColumns).save(`${fileStamp(practiceName, program)}.pdf`);
  };

  const adminLabel = (admin) => {
    const name = [admin.last_name, admin.first_name].filter(Boolean).join(', ').trim();
    return name || admin.username || 'Practice admin';
  };

  const adminInitials = (admin) => {
    const first = String(admin.first_name || '').trim().charAt(0);
    const last = String(admin.last_name || '').trim().charAt(0);
    const letters = `${first}${last}`.toUpperCase();
    return letters || adminLabel(admin).charAt(0).toUpperCase() || 'A';
  };

  const openAdminPicker = async () => {
    if (!displayRows.length) {
      toast.info('Generate the sheet before emailing it');
      return;
    }
    if (!practiceId) {
      toast.warning('Practice is required to email the report');
      return;
    }
    setShowAdminPicker(true);
    setAdminPickerTab('recipients');
    setEmailColOpen(false);
    if (pdfPreviewBlobUrl) {
      URL.revokeObjectURL(pdfPreviewBlobUrl);
      setPdfPreviewBlobUrl(null);
    }
    setSelectedAdminIds([]);
    setCustomRecipients([]);
    setCustomEmailDraft('');
    setAdminsLoading(true);
    try {
      const response = await adminService.getAdmins(practiceId, { page: 1, limit: 200 });
      const payload = response?.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.admins) ? payload.admins : []);
      const activeAdmins = list.filter((admin) => String(admin.status || '').trim().toLowerCase() === 'active');
      setAdmins(activeAdmins);
    } catch (error) {
      console.error('Failed to load practice admins:', error);
      toast.error('Failed to load practice admins');
      setAdmins([]);
    } finally {
      setAdminsLoading(false);
    }
  };

  const toggleAdmin = (adminId) => {
    setSelectedAdminIds((current) => (
      current.includes(adminId)
        ? current.filter((id) => id !== adminId)
        : [...current, adminId]
    ));
  };

  const addCustomRecipient = () => {
    const emails = splitEmailDraft(customEmailDraft);
    if (!emails.length) {
      setCustomEmailError('Enter at least one email address.');
      return;
    }
    const invalidEmail = emails.find((email) => !isEmail(email));
    if (invalidEmail) {
      setCustomEmailError(getEmailValidationError(invalidEmail, { required: true }));
      return;
    }

    const existingEmails = new Set([
      ...customRecipients.map((item) => item.email.toLowerCase()),
      ...admins.map((admin) => String(admin.email || '').trim().toLowerCase())
    ]);
    const uniqueEmails = [...new Set(emails.map((email) => email.toLowerCase()))];
    const duplicate = uniqueEmails.find((email) => existingEmails.has(email));
    if (duplicate) {
      setCustomEmailError(`${duplicate} is already in the recipient list.`);
      return;
    }

    setCustomRecipients((current) => [...current, ...emails.map((email, index) => ({
      id: `custom-${Date.now()}-${index}`,
      email,
      selected: true
    }))]);
    setCustomEmailDraft('');
    setCustomEmailError('');
  };

  const sendReportToAdmins = async () => {
    const selectedAdmins = admins
      .filter((admin) => selectedAdminIds.includes(admin.id))
      .map((admin) => ({
        id: admin.id,
        label: adminLabel(admin),
        email: String(admin.email || '').trim()
      }));
    const selectedCustom = customRecipients.filter((item) => item.selected);
    if (!selectedAdmins.length && !selectedCustom.length) {
      toast.warning('Select at least one recipient');
      return;
    }
    const invalidAdmin = selectedAdmins.find((item) => !isEmail(item.email));
    const invalidCustom = selectedCustom.find((item) => !isEmail(item.email));
    if (invalidAdmin || invalidCustom) {
      toast.warning(invalidAdmin
        ? `${invalidAdmin.label} does not have a valid email on file`
        : 'Enter a valid email for every selected recipient');
      return;
    }
    const recipients = [...new Set([
      ...selectedAdmins.map((item) => item.email),
      ...selectedCustom.map((item) => item.email.trim())
    ])];
    setSendingEmail(true);
    try {
      if (!validateCptPricing()) return;
      const pdf = buildSheetPdf(selectedEmailColumns);
      const file = new File(
        [pdf.output('blob')],
        `${fileStamp(practiceName, program)}.pdf`,
        { type: 'application/pdf' }
      );
      const content = [
        `Attached is the ${programLabel} billing report for ${practiceName}.`,
        `Billing period: ${periodRange || periodLabel || 'Current period'}`,
        `Patients: ${displayRows.length}`
      ].join('\n');
      const result = await sendReportEmail({
        to: recipients.join(', '),
        subject: `${programLabel} Billing Report - ${practiceName} (${periodLabel || 'Current period'})`,
        content,
        attachments: [file]
      }, { event_id: 12 });
      if (!result.success) {
        throw new Error(result.error || result.message || 'Failed to send email');
      }
      try {
        const archive = new FormData();
        archive.append('file', file);
        archive.append('practiceId', String(practiceId));
        archive.append('program', String(program || 'rpm'));
        archive.append('periodLabel', periodLabel || periodRange || '');
        archive.append('periodStart', String(periodStart || '').slice(0, 10));
        archive.append('periodEnd', String(periodEnd || '').slice(0, 10));
        archive.append('subject', `${programLabel} Billing Report - ${practiceName} (${periodLabel || 'Current period'})`);
        archive.append('patientCount', String(displayRows.length));
        archive.append('recipients', JSON.stringify([
          ...selectedAdmins.map((admin) => ({ email: admin.email, name: admin.label, userId: admin.id })),
          ...selectedCustom.map((item) => ({ email: item.email.trim(), name: '' }))
        ]));
        await billingService.archiveSentReport(archive);
      } catch (archiveError) {
        console.error('Failed to store billing report:', archiveError);
        toast.warning('The email was sent, but the report copy could not be saved.');
      }
      toast.success(`Billing report sent to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`);
      closeAdminPicker();
    } catch (error) {
      console.error('Failed to email billing report:', error);
      toast.error(error?.message || 'Failed to email the billing report');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleEmailColumn = (column) => {
    if (isMandatoryEmailColumn(column)) return;
    setSelectedEmailColumns((current) => (
      current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column]
    ));
  };

  const toggleAllEmailColumns = () => {
    const optional = headers.filter((column) => !isMandatoryEmailColumn(column));
    const allOptionalSelected = optional.every((column) => selectedEmailColumns.includes(column));
    setSelectedEmailColumns(allOptionalSelected
      ? headers.filter(isMandatoryEmailColumn)
      : [...headers]);
  };

  const closeAdminPicker = () => {
    setShowAdminPicker(false);
    setAdminPickerTab('recipients');
    if (pdfPreviewBlobUrl) {
      URL.revokeObjectURL(pdfPreviewBlobUrl);
      setPdfPreviewBlobUrl(null);
    }
  };

  const openPdfPreviewInModal = () => {
    try {
      const pdf = buildSheetPdf(selectedEmailColumns);
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      if (pdfPreviewBlobUrl) {
        URL.revokeObjectURL(pdfPreviewBlobUrl);
      }
      setPdfPreviewBlobUrl(url);
      setAdminPickerTab('preview');
    } catch (error) {
      console.error('Failed to preview PDF:', error);
      toast.error('Failed to generate PDF preview');
    }
  };

  const moveSection = (row) => {
    const nextSection = row.section === 'noncompliant' ? 'standard' : 'noncompliant';
    const restoredCodes = nextSection === 'standard' && (!row.cptCodes || row.cptCodes.length === 0)
      ? (row.savedCptCodes || [])
      : row.cptCodes;
    updateRow(row.key, {
      section: nextSection,
      sectionManual: true,
      cptCodes: restoredCodes
    });
  };

  if (!isOpen) return null;

  const visibleHeaders = headers.filter((header) => (
    selectedEmailColumns.includes(header) || isMandatoryEmailColumn(header)
  ));
  const pricingCodes = Object.keys(cptCodePatientCounts)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const receiptPricingPatientCount = rows.filter(
    (row) => row.section !== 'noncompliant' && (row.cptCodes || []).some((code) => String(code || '').trim())
  ).length;
  const receiptPricesFilledCount = pricingCodes.filter((code) => (
    numberOrNull(cptPrices[code]) != null && Number(cptPrices[code]) >= 0
  )).length;
  const colSpan = visibleHeaders.length + (isRpm ? 1 : 0);

  const renderSheetCell = (header, row, cptPills) => {
    if (header === 'Status') {
      return <td key={header}><StatusBadge status={row.status || 'N/A'} /></td>;
    }
    if (header === 'Name') {
      return <td key={header}><strong>{row.patientName || '—'}</strong></td>;
    }
    if (header === 'Provider') {
      return <td key={header}>{row.provider || '—'}</td>;
    }
    if (header === 'Caregiver') {
      return <td key={header}>{row.caregiver || '—'}</td>;
    }
    if (header === 'Serial #') {
      return <td key={header}>{row.serial || '—'}</td>;
    }
    if (header === 'Vitals') {
      return <td key={header} className="gbm-vitals-cell">{row.vitals || '—'}</td>;
    }
    if (header === 'RPM Start Date') {
      return <td key={header}>{row.rpmStartDate || '—'}</td>;
    }
    if (header === 'Clinical Min') {
      return (
        <td key={header}>
          <input
            className="gbm-inline"
            inputMode="numeric"
            value={row.clinicalMinutes}
            onChange={(event) => updateRow(row.key, { clinicalMinutes: event.target.value })}
            aria-label="Clinical minutes"
          />
        </td>
      );
    }
    if (header === '# Of Readings') {
      return (
        <td key={header}>
          <input
            className="gbm-inline gbm-inline-num"
            inputMode="numeric"
            value={row.readings}
            onChange={(event) => updateRpmMeasures(row, { readings: event.target.value })}
            aria-label="Number of readings"
          />
        </td>
      );
    }
    if (header === 'Provider Min') {
      return (
        <td key={header}>
          <input
            className="gbm-inline gbm-inline-num"
            inputMode="numeric"
            value={row.providerMinutes}
            onChange={(event) => updateRow(row.key, { providerMinutes: event.target.value })}
            aria-label="Provider minutes"
          />
        </td>
      );
    }
    if (header === 'Total Service Time' || header === 'Service Time') {
      return (
        <td key={header}>
          <input
            className="gbm-inline gbm-inline-num"
            inputMode="numeric"
            value={row.serviceTime}
            onChange={(event) => (isRpm
              ? updateRpmMeasures(row, { serviceTime: event.target.value })
              : updateRow(row.key, { serviceTime: event.target.value }))}
            aria-label="Service time"
          />
        </td>
      );
    }
    if (header === 'Eligible CPT codes') {
      return (
        <td key={header}>
          <div className="gbm-cpt-pills" aria-label={`CPT codes for ${row.patientName || 'patient'}`}>
            {cptPills.length === 0 && (
              <span className="gbm-cpt-empty">—</span>
            )}
            {cptPills.map((entry) => (
              <span className="gbm-cpt-pill" key={`${row.key}-${entry.code}-${entry.occurrence}`}>
                {entry.code}
                <button
                  type="button"
                  className="gbm-cpt-pill-remove"
                  aria-label={`Remove ${entry.code}`}
                  onClick={() => removeCptOccurrence(row, entry.code, entry.occurrence)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </td>
      );
    }
    if (header === 'Price') {
      const price = priceForRow(row);
      return <td key={header} className="gbm-price-cell">{price == null ? '—' : `$${price.toFixed(2)}`}</td>;
    }
    return null;
  };

  return (
    <div className="gbm-overlay" role="presentation">
      <div
        className="gbm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="generate-billing-title"
      >
        <div className="gbm-header">
          <div className="gbm-header-copy">
            <h2 id="generate-billing-title">Generate Billing</h2>
            <p className="gbm-header-cycle">
              {dos} · {periodRange}
            </p>
          </div>
          <button type="button" className="gbm-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="gbm-summary">
          <div className="gbm-toolbar-left">
            <label className="gbm-pricing-toggle">
              <input
                type="checkbox"
                checked={cptPricingEnabled}
                onChange={(event) => setCptPricingEnabled(event.target.checked)}
                aria-label="Enable pricing"
              />
              <span className="gbm-switch-track" aria-hidden="true" />
              <span className="gbm-switch-label">Enable pricing</span>
            </label>
            <button
              type="button"
              className="gbm-filters-toggle"
              aria-expanded={filtersExpanded}
              aria-controls="gbm-filter-content"
              onClick={() => setFiltersExpanded((expanded) => !expanded)}
            >
              <FontAwesomeIcon icon={faChevronDown} />
              {filtersExpanded ? 'Hide filters' : 'Show filters'}
            </button>
            <span className="gbm-status-pill gbm-patients-pill">
              <strong>{displayRows.length}</strong> patients
            </span>
            {isRpm && (
              <span className={`gbm-status-pill gbm-noncompliant-pill${nonCompliantCount > 0 ? ' has-noncompliant' : ''}`}>
                <span className="gbm-status-dot" aria-hidden="true" />
                <strong>{nonCompliantCount}</strong> noncompliant
              </span>
            )}
          </div>
          <div className="gbm-summary-actions">
            <button
              type="button"
              className="gbm-report-btn"
              onClick={openAdminPicker}
              disabled={!displayRows.length || sendingEmail}
            >
              Email report
            </button>
            <div className="gbm-export" ref={exportMenuRef}>
              <button
                type="button"
                className="gbm-report-btn"
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={!displayRows.length}
                aria-expanded={exportMenuOpen}
                aria-haspopup="menu"
              >
                Export
              </button>
              {exportMenuOpen && (
                <div className="gbm-export-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => { setExportMenuOpen(false); handleExportPdf(); }}>
                    Export as PDF
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setExportMenuOpen(false); handleExportCsv(); }}>
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`gbm-filters${filtersExpanded ? '' : ' is-collapsed'}`}>
          <div id="gbm-filter-content" className="gbm-filter-content" hidden={!filtersExpanded}>
          <div className="gbm-filter-grid">
            <div className="gbm-field">
              <label htmlFor="gbm-practice-name">Practice</label>
              <input
                id="gbm-practice-name"
                type="text"
                className="gbm-readonly-field"
                value={practiceName || 'Practice'}
                readOnly
                aria-readonly="true"
              />
            </div>

            <div className="gbm-field">
              <label htmlFor="gbm-status">Status</label>
              <select
                id="gbm-status"
                value={filters.status}
                onChange={(event) => setFilter('status', event.target.value)}
              >
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="LOCK">LOCK</option>
              </select>
            </div>

            <div className="gbm-field">
              <label htmlFor="gbm-name">Patient name</label>
              <input
                id="gbm-name"
                type="text"
                value={filters.name}
                placeholder="Search name"
                onChange={(event) => setFilter('name', event.target.value)}
              />
            </div>

            {isRpm && (
              <div className="gbm-field">
                <label htmlFor="gbm-readings-min">Readings min</label>
                <input
                  id="gbm-readings-min"
                  type="number"
                  min="0"
                  value={filters.readingsMin}
                  placeholder="Min"
                  onChange={(event) => setFilter('readingsMin', event.target.value)}
                />
              </div>
            )}

            {isRpm && (
              <div className="gbm-field">
                <label htmlFor="gbm-readings-max">Readings max</label>
                <input
                  id="gbm-readings-max"
                  type="number"
                  min="0"
                  value={filters.readingsMax}
                  placeholder="Max"
                  onChange={(event) => setFilter('readingsMax', event.target.value)}
                />
              </div>
            )}

            {!isRpm && (
              <div className="gbm-field">
                <label htmlFor="gbm-clinical-min">Clinical min</label>
                <input
                  id="gbm-clinical-min"
                  type="number"
                  min="0"
                  value={filters.clinicalMin}
                  placeholder="Min"
                  onChange={(event) => setFilter('clinicalMin', event.target.value)}
                />
              </div>
            )}

            {!isRpm && (
              <div className="gbm-field">
                <label htmlFor="gbm-clinical-max">Clinical max</label>
                <input
                  id="gbm-clinical-max"
                  type="number"
                  min="0"
                  value={filters.clinicalMax}
                  placeholder="Max"
                  onChange={(event) => setFilter('clinicalMax', event.target.value)}
                />
              </div>
            )}

            <div className="gbm-field">
              <label htmlFor="gbm-service-min">Service time min</label>
              <input
                id="gbm-service-min"
                type="number"
                min="0"
                value={filters.serviceMin}
                placeholder="0"
                onChange={(event) => setFilter('serviceMin', event.target.value)}
              />
            </div>

            <div className="gbm-field">
              <label htmlFor="gbm-service-max">Service time max</label>
              <input
                id="gbm-service-max"
                type="number"
                min="0"
                value={filters.serviceMax}
                placeholder="Any"
                onChange={(event) => setFilter('serviceMax', event.target.value)}
              />
            </div>

            <div className="gbm-field gbm-field-cpt">
              <label htmlFor="gbm-cpt-codes">Specific CPT</label>
              <div className="gbm-multiselect" ref={cptMenuRef}>
                <button
                  id="gbm-cpt-codes"
                  type="button"
                  className="gbm-multiselect-trigger"
                  onClick={() => setCptMenuOpen((open) => !open)}
                >
                  <span>
                    {allCptSelected ? 'All' : (filters.cptCodes.length ? filters.cptCodes.join(', ') : 'Any code')}
                  </span>
                  <span>{cptMenuOpen ? '▲' : '▼'}</span>
                </button>
                {cptMenuOpen && (
                  <div className="gbm-multiselect-menu">
                    <button
                      type="button"
                      className={`gbm-multiselect-option${allCptSelected ? ' selected' : ''}`}
                      onClick={toggleAllFilterCpt}
                    >
                      All
                    </button>
                    {catalog.map((code) => (
                      <button
                        key={code}
                        type="button"
                        className={`gbm-multiselect-option${filters.cptCodes.includes(code) ? ' selected' : ''}`}
                        onClick={() => toggleFilterCpt(code)}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="gbm-field gbm-field-columns">
              <label>Select Columns to Include in Billing</label>
              <div className="email-col-dropdown" ref={emailColRef}>
                <div
                  className="email-col-trigger"
                  onClick={() => setEmailColOpen((open) => !open)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') setEmailColOpen((open) => !open);
                  }}
                >
                  <span className="email-col-trigger-label">
                    {`${selectedEmailColumns.length} of ${headers.length} selected`}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`email-col-chevron${emailColOpen ? ' email-col-chevron--open' : ''}`}
                  />
                </div>
                {emailColOpen && (
                  <div className="email-col-panel">
                    <div className="email-col-panel-header">
                      <button
                        type="button"
                        className="email-columns-toggle-btn"
                        onClick={toggleAllEmailColumns}
                      >
                        {selectedEmailColumns.filter((column) => !isMandatoryEmailColumn(column)).length
                          === headers.filter((column) => !isMandatoryEmailColumn(column)).length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                    <div className="email-col-options">
                      {headers.map((column) => {
                        const mandatory = isMandatoryEmailColumn(column);
                        const checked = selectedEmailColumns.includes(column);
                        return (
                          <label
                            key={column}
                            className={`email-col-option${mandatory ? ' email-col-option--locked' : ''}`}
                            title={mandatory ? 'This column is required and cannot be removed' : ''}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={mandatory}
                              onChange={() => toggleEmailColumn(column)}
                            />
                            <span>{column}</span>
                            {mandatory && <span className="email-col-lock-badge">Required</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="gbm-filter-actions">
            <div className="gbm-filter-buttons">
              {!filtersAreDefault(filters) && (
                <button
                  type="button"
                  className="gbm-reset"
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {isRpm && (
            <div className="gbm-noncompliant-option">
              <label className="gbm-check">
                <input
                  type="checkbox"
                  checked={filters.includeNonCompliant}
                  onChange={(event) => setFilter('includeNonCompliant', event.target.checked)}
                />
                <span>Show noncompliant patients</span>
              </label>
            </div>
          )}
          </div>
        </div>

        {cptPricingEnabled && (
        <div className="gbm-pricing-panel">
          <section className="gbm-cpt-pricing" aria-label="CPT code pricing">
            <header className="gbm-cpt-pricing-header">
              <div>
                <h3>Price per CPT code</h3>
                <p>Enter the rate for each code before generating.</p>
              </div>
              <span className="gbm-cpt-pricing-overview">
                {receiptPricingPatientCount} patients across {pricingCodes.length} codes
              </span>
            </header>
            {pricingCodes.length > 0 ? (
              <>
                <div className="gbm-cpt-pricing-list">
                    {pricingCodes.map((code) => {
                      const patientCount = cptCodePatientCounts[code] || 0;
                      return (
                        <div className="gbm-cpt-price-item" key={code}>
                          <div className="gbm-cpt-price-code">
                            <span className="gbm-cpt-code-chip">{code}</span>
                          </div>
                          <span className="gbm-cpt-price-count">
                            <strong>{patientCount}</strong> patient{patientCount === 1 ? '' : 's'}
                          </span>
                          <label className="gbm-cpt-price-input-wrap">
                            <span>$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={cptPrices[code] ?? ''}
                              onChange={(event) => {
                                setCptPrices((current) => ({
                                  ...current,
                                  [code]: event.target.value
                                }));
                              }}
                              aria-label={`Price for CPT ${code}`}
                            />
                          </label>
                        </div>
                      );
                    })}
                </div>
                <footer className="gbm-pricing-panel-actions">
                  <span className="gbm-cpt-pricing-filled-count">
                    {receiptPricesFilledCount} of {pricingCodes.length} prices entered
                  </span>
                  <button
                    type="button"
                    className="gbm-receipt-btn"
                    onClick={handleGenerateReceipt}
                    disabled={!hasGenerated || loading}
                  >
                    Generate receipt
                  </button>
                </footer>
              </>
                ) : (
                  <p className="gbm-cpt-pricing-empty">No CPT codes are available for this billing program.</p>
                )}
          </section>
        </div>
        )}

        <div className="gbm-body">
          {loading && !hasGenerated && (
            <div className="gbm-body-status">
              <p className="gbm-empty">Loading the billing sheet…</p>
            </div>
          )}
          {!hasGenerated && !loading && (
            <div className="gbm-body-status">
              <p className="gbm-empty">
                The billing sheet could not be loaded.
              </p>
              <button type="button" className="gbm-generate" onClick={handleGenerate}>
                Try again
              </button>
            </div>
          )}

          {hasGenerated && (
            <>
              <div className="reusable-table-container gbm-table-card">
                <div className="reusable-table-responsive">
                  <table className="reusable-table gbm-edit-table">
                    <thead>
                      <tr>
                        {visibleHeaders.map((header) => (
                          <th key={header}>{header}</th>
                        ))}
                        {isRpm && <th className="gbm-remove-col" />}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.length === 0 && (
                        <tr>
                          <td colSpan={colSpan} className="gbm-none">No patients matched these filters.</td>
                        </tr>
                      )}
                      {displayRows.map((row, index) => {
                        const showBanner = row.section === 'noncompliant'
                          && displayRows[index - 1]?.section !== 'noncompliant';
                        const cptPills = cptPillEntries(cptCodesForDisplay(row), catalog);
                        return (
                          <React.Fragment key={row.key}>
                            {showBanner && (
                              <tr className="gbm-split">
                                <td colSpan={colSpan}>Noncompliant Patients</td>
                              </tr>
                            )}
                            <tr className={row.section === 'noncompliant' ? 'gbm-row-noncompliant' : undefined}>
                              {visibleHeaders.map((header) => renderSheetCell(header, row, cptPills))}
                              {isRpm && (
                                <td className="gbm-row-actions">
                                  <button
                                    type="button"
                                    className="gbm-move"
                                    onClick={() => moveSection(row)}
                                  >
                                    {row.section === 'noncompliant' ? 'Mark compliant' : 'Mark noncompliant'}
                                  </button>
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {showAdminPicker && (
          <div className="gbm-admin-overlay" role="presentation">
            <div className={`gbm-admin-dialog${adminPickerTab === 'preview' ? ' is-previewing' : ''}`} role="dialog" aria-modal="true" aria-labelledby="gbm-admin-title">
              <div className="gbm-admin-head">
                <div className="gbm-admin-title-area">
                  <h3 id="gbm-admin-title">{adminPickerTab === 'preview' ? 'PDF Preview' : 'Email report'}</h3>
                  
                </div>
                <button
                  type="button"
                  className="gbm-admin-close"
                  onClick={adminPickerTab === 'preview' ? () => setAdminPickerTab('recipients') : closeAdminPicker}
                  aria-label={adminPickerTab === 'preview' ? 'Back to recipients' : 'Close'}
                  title={adminPickerTab === 'preview' ? 'Back to recipients' : 'Close'}
                >
                  ×
                </button>
              </div>

              {adminPickerTab === 'preview' ? (
                <div className="gbm-admin-preview-pane">
                  {pdfPreviewBlobUrl ? (
                    <iframe
                      src={pdfPreviewBlobUrl}
                      title="Billing Report PDF Preview"
                      className="gbm-admin-preview-iframe"
                    />
                  ) : (
                    <div className="gbm-admin-preview-loading">Generating preview…</div>
                  )}
                </div>
              ) : (
                <div className="gbm-admin-scroll">
                  <section className="gbm-admin-section">
                    <div className="gbm-admin-section-label">Practice admins</div>
                  {adminsLoading && <p className="gbm-admin-note">Loading practice admins…</p>}
                  {!adminsLoading && admins.length === 0 && (
                    <p className="gbm-admin-note">No practice admins were found. Add an email below.</p>
                  )}
                  {!adminsLoading && admins.length > 0 && (
                    <ul className="gbm-recipient-list">
                      {admins.map((admin) => {
                        const email = String(admin.email || '').trim();
                        const checked = selectedAdminIds.includes(admin.id);
                        const canSelect = Boolean(email) && !sendingEmail;
                        return (
                          <li key={admin.id}>
                            <div
                              className={`gbm-recipient${checked ? ' is-selected' : ''}`}
                              onClick={() => { if (canSelect) toggleAdmin(admin.id); }}
                            >
                              <input
                                type="checkbox"
                                className="gbm-admin-check"
                                checked={checked}
                                disabled={!canSelect}
                                onClick={(event) => event.stopPropagation()}
                                onChange={() => { if (canSelect) toggleAdmin(admin.id); }}
                                aria-label={`Send to ${adminLabel(admin)}`}
                              />
                              <span className="gbm-recipient-avatar" aria-hidden="true">{adminInitials(admin)}</span>
                              <div className="gbm-recipient-copy">
                                <strong className="gbm-admin-name">{adminLabel(admin)}</strong>
                                <span className="gbm-admin-email-text">{email || 'No email on file'}</span>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>

                <section className="gbm-admin-section">
                  <div className="gbm-admin-section-label">Other recipients</div>
                  {customRecipients.length > 0 && (
                    <ul className="gbm-recipient-list">
                      {customRecipients.map((recipient) => (
                        <li key={recipient.id}>
                          <div
                            className={`gbm-recipient gbm-recipient-extra${recipient.selected ? ' is-selected' : ''}`}
                            onClick={() => {
                              if (sendingEmail) return;
                              setCustomRecipients((current) => current.map((item) => (
                                item.id === recipient.id ? { ...item, selected: !item.selected } : item
                              )));
                            }}
                          >
                            <input
                              type="checkbox"
                              className="gbm-admin-check"
                              checked={recipient.selected}
                              disabled={sendingEmail}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => setCustomRecipients((current) => current.map((item) => (
                                item.id === recipient.id ? { ...item, selected: !item.selected } : item
                              )))}
                              aria-label={`Send to ${recipient.email || 'custom email'}`}
                            />
                            <div className="gbm-recipient-copy">
                              <input
                                type="text"
                                inputMode="email"
                                autoComplete="off"
                                spellCheck={false}
                                name={`billing-extra-email-${recipient.id}`}
                                className="gbm-admin-email"
                                value={recipient.email}
                                disabled={sendingEmail}
                                aria-label="Additional email"
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => setCustomRecipients((current) => current.map((item) => (
                                  item.id === recipient.id ? { ...item, email: event.target.value } : item
                                )))}
                              />
                            </div>
                            <button
                              type="button"
                              className="gbm-admin-remove"
                              disabled={sendingEmail}
                              aria-label="Remove email"
                              onClick={(event) => {
                                event.stopPropagation();
                                setCustomRecipients((current) => current.filter((item) => item.id !== recipient.id));
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="gbm-admin-add-row">
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="off"
                      spellCheck={false}
                      name="billing-extra-email-draft"
                      className="gbm-admin-email"
                      value={customEmailDraft}
                      disabled={sendingEmail}
                      placeholder="Enter emails, separated by commas"
                      aria-label="Additional email address"
                      onChange={(event) => {
                        const rawValue = event.target.value;
                        const parts = rawValue.split(',');
                        const sanitized = parts
                          .map((part, index) => (index === parts.length - 1
                            ? sanitizeEmailInput(part)
                            : part.trim()))
                          .join(', ');
                        setCustomEmailDraft(sanitized);
                        setCustomEmailError('');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addCustomRecipient();
                        }
                      }}
                    />
                    <button type="button" onClick={addCustomRecipient} disabled={sendingEmail || !customEmailDraft.trim()}>
                      Add
                    </button>
                  </div>
                  {customEmailError && (
                    <p className="gbm-email-validation-error" role="alert">{customEmailError}</p>
                  )}
                </section>
              </div>
              )}
              {adminPickerTab !== 'preview' && (
                <div className="gbm-admin-actions">
                  <span className="gbm-admin-count">
                    {selectedAdminIds.length + customRecipients.filter((item) => item.selected).length} selected
                  </span>
                  <button type="button" onClick={closeAdminPicker} disabled={sendingEmail}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="gbm-admin-send"
                    onClick={sendReportToAdmins}
                    disabled={sendingEmail || (selectedAdminIds.length === 0 && !customRecipients.some((item) => item.selected))}
                  >
                    {sendingEmail ? 'Sending…' : 'Send PDF'}
                  </button>
                  <button
                    type="button"
                    className="gbm-admin-preview"
                    onClick={openPdfPreviewInModal}
                    disabled={sendingEmail}
                    title="Preview PDF report inside this modal"
                  >
                    Preview PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showReceiptPreview && (
          <div className="gbm-receipt-overlay" role="presentation">
            <section
              className="gbm-receipt-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="gbm-receipt-title"
            >
              <header className="gbm-receipt-header">
                <div className="gbm-receipt-header-copy">
                  <h2 id="gbm-receipt-title">Receipt Preview</h2>
                  <p>
                    <strong>{practiceName || 'Practice'}</strong>
                    <span className="gbm-receipt-sep">·</span>
                    <span>{programLabel}</span>
                    {periodRange && (
                      <>
                        <span className="gbm-receipt-sep">·</span>
                        <span className="gbm-receipt-period">{periodRange}</span>
                      </>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="gbm-admin-close"
                  onClick={() => setShowReceiptPreview(false)}
                  aria-label="Close receipt preview"
                >
                  ×
                </button>
              </header>

              <div className="gbm-receipt-content">
                <div className="gbm-receipt-table-wrap">
                  <table className="gbm-receipt-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Service Date</th>
                        <th>Product / Service</th>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Rate</th>
                        <th className="gbm-receipt-th-amount">Amount</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {receiptDraft.map((line, index) => (
                        <tr key={line.id}>
                          <td>{index + 1}</td>
                          <td>
                            <AppDateInput
                              value={line.serviceDate}
                              onChange={(v) => updateReceiptLine(line.id, 'serviceDate', v)}
                              aria-label={`Service date for line ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={line.product}
                              onChange={(event) => updateReceiptLine(line.id, 'product', event.target.value)}
                              aria-label={`Product or service for line ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={line.description}
                              onChange={(event) => updateReceiptLine(line.id, 'description', event.target.value)}
                              aria-label={`Description for line ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={line.quantity}
                              onChange={(event) => updateReceiptLine(line.id, 'quantity', event.target.value)}
                              aria-label={`Quantity for line ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.rate}
                              onChange={(event) => updateReceiptLine(line.id, 'rate', event.target.value)}
                              aria-label={`Rate for line ${index + 1}`}
                            />
                          </td>
                          <td className="gbm-receipt-amount">${receiptLineAmount(line).toFixed(2)}</td>
                          <td>
                            <button
                              type="button"
                              className="gbm-receipt-remove"
                              onClick={() => removeReceiptLine(line.id)}
                              aria-label={`Remove line ${index + 1}`}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="6">Total</td>
                        <td className="gbm-receipt-amount">
                          ${receiptDraft.reduce((sum, line) => sum + receiptLineAmount(line), 0).toFixed(2)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <button type="button" className="gbm-receipt-add-line" onClick={addReceiptLine}>
                  + Add item
                </button>
              </div>

              <footer className="gbm-receipt-actions">
                <button
                  type="button"
                  className="gbm-receipt-cancel-btn"
                  onClick={() => setShowReceiptPreview(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="gbm-receipt-save-btn"
                  onClick={handleSaveReceipt}
                >
                  Save receipt PDF
                </button>
              </footer>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateBillingModal;
